import Supercluster from "supercluster";

export const CLUSTER_BBOX_WORLD = [-180, -85, 180, 85];

export const CLUSTER_MAX_ZOOM = 24;

export const CLUSTER_RADIUS = 48;

export const CLUSTER_CATEGORIES = {
  people: {
    pointKind: "person",
    clusterKind: "person-cluster",
    outputCategory: "people",
    iconType: "person-cluster",
  },
  animals: {
    pointKind: "animal",
    clusterKind: "animal-cluster",
    outputCategory: "animals",
    iconType: "animals-cluster",
    dotColor: "#7c3aed",
  },
  environment: {
    pointKind: "environment",
    clusterKind: "environment-cluster",
    outputCategory: "environment",
    iconType: "environment-cluster",
    dotColor: "#0891b2",
  },
};

export function buildCategoryClusterIndexes(categoryFeatures, options = {}) {
  return Object.freeze(
    Object.fromEntries(
      Object.entries(categoryFeatures).map(([category, features]) => [
        category,
        buildCategoryClusterIndex(category, features, options),
      ])
    )
  );
}

export function buildCategoryClusterIndex(category, features, options = {}) {
  const config = categoryConfig(category);
  const baseGeneration = options.baseGeneration ?? 0;
  const pointFeatures = (features || []).map((feature, index) =>
    copyPointFeatureForClustering(feature, category, index, config)
  );
  const sourceByInternalId = new Map(
    pointFeatures.map((feature) => [feature.properties.clusterInternalId, feature])
  );
  const index = new Supercluster({
    radius: options.radius ?? CLUSTER_RADIUS,
    maxZoom: options.maxZoom ?? CLUSTER_MAX_ZOOM,
    minPoints: 2,
    map: clusterPropertyMap,
    reduce: reduceClusterProperties,
  }).load(pointFeatures);

  return Object.freeze({
    category,
    baseGeneration,
    config,
    index,
    pointFeatures: Object.freeze(pointFeatures),
    sourceByInternalId,
  });
}

export function renderCategoryClusters(categoryIndex, zoom, options = {}) {
  const queryZoom = flooredZoom(zoom);
  const bbox = options.bbox || CLUSTER_BBOX_WORLD;
  const results = categoryIndex.index.getClusters(bbox, queryZoom);
  const unclusteredIds = new Set();
  const clusterFeatures = [];

  results.forEach((feature) => {
    if (feature.properties?.cluster) {
      clusterFeatures.push(toSyntheticClusterFeature(feature, categoryIndex));
      return;
    }
    const internalId = feature.properties?.clusterInternalId;
    if (internalId) unclusteredIds.add(internalId);
  });

  return {
    zoom: queryZoom,
    clusterFeatures,
    unclusteredIds,
    pointFeatures: copyRenderedPointFeatures(categoryIndex.pointFeatures, unclusteredIds),
  };
}

export function buildClusteredFeatureCollectionForZoom(baseData, zoom, options = {}) {
  const people = renderCategoryClusters(baseData.clusterIndexes.people, zoom, options);
  const animals = renderCategoryClusters(baseData.clusterIndexes.animals, zoom, options);
  const environment = renderCategoryClusters(baseData.clusterIndexes.environment, zoom, options);

  return {
    type: "FeatureCollection",
    features: [
      ...(baseData.zipFeatures || []),
      ...(baseData.countyFeatures || []),
      ...(baseData.pieFeatures || []),
      ...people.clusterFeatures,
      ...animals.clusterFeatures,
      ...environment.clusterFeatures,
      ...people.pointFeatures,
      ...animals.pointFeatures,
      ...environment.pointFeatures,
    ],
  };
}

export function getCategoryClusterExpansionZoom(categoryIndex, clusterId) {
  return categoryIndex.index.getClusterExpansionZoom(clusterId);
}

export function clusterLookupKey(category, clusterId) {
  return `${category}:${clusterId}`;
}

export function flooredZoom(zoom) {
  const value = Math.floor(Number(zoom));
  return Number.isFinite(value) ? value : 0;
}

export function toSyntheticClusterFeature(feature, categoryIndex) {
  const props = feature.properties || {};
  const category = categoryIndex.category;
  const config = categoryIndex.config;
  const pointCount = toNumber(props.point_count);
  const sumTotal = toNumber(props.sum_total);
  const sumSick = toNumber(props.sum_sick);
  const isPeople = category === "people";
  const total = isPeople ? sumTotal : pointCount;
  const sickRate = sumTotal > 0 ? sumSick / sumTotal : 0;

  return {
    type: "Feature",
    geometry: cloneJson(feature.geometry),
    properties: {
      kind: config.clusterKind,
      category: config.outputCategory,
      clusterCategory: category,
      clusterId: props.cluster_id,
      clusterBaseGeneration: categoryIndex.baseGeneration,
      point_count: pointCount,
      total_count: total,
      sick_count: sumSick,
      healthy_count: isPeople ? toNumber(props.healthy_count) : 0,
      people_sick_count: isPeople ? toNumber(props.people_sick_count) : 0,
      sum_total: sumTotal,
      sum_sick: sumSick,
      sick_rate: sickRate,
      clusterLabel: String(total),
      iconType: config.iconType,
      dotColor: isPeople ? sickRateCssColor(sickRate) : config.dotColor,
      dotOpacity: 0.9,
      radius: isPeople ? peopleClusterRadius(total) : categoryClusterRadius(total),
    },
  };
}

export function copyRenderedPointFeatures(pointFeatures, unclusteredIds) {
  return pointFeatures.map((feature) => {
    const properties = cloneJson(feature.properties || {});
    const internalId = properties.clusterInternalId;
    return {
      ...feature,
      geometry: cloneJson(feature.geometry),
      properties: {
        ...properties,
        clusterSize: unclusteredIds.has(internalId) ? 1 : 2,
      },
    };
  });
}

function copyPointFeatureForClustering(feature, category, index, config) {
  const properties = cloneJson(feature.properties || {});
  const geometry = cloneJson(feature.geometry);
  return {
    type: "Feature",
    id: stableInternalId(feature, category, index),
    geometry,
    properties: {
      ...properties,
      kind: properties.kind || config.pointKind,
      clusterCategory: category,
      clusterInternalId: stableInternalId(feature, category, index),
    },
  };
}

function stableInternalId(feature, category, index) {
  const properties = feature.properties || {};
  const explicitId =
    feature.id ??
    properties.clusterInternalId ??
    properties.id ??
    properties.report_id ??
    properties.zip_code;
  if (explicitId != null && explicitId !== "") return `${category}:${String(explicitId)}`;
  const coordinates = feature.geometry?.coordinates || [];
  return `${category}:${index}:${coordinates[0] ?? ""}:${coordinates[1] ?? ""}`;
}

function clusterPropertyMap(properties) {
  return {
    sum_total: toNumber(properties.total_count),
    sum_sick: toNumber(properties.sick_count),
    healthy_count: toNumber(properties.healthy_count),
    people_sick_count: toNumber(properties.people_sick_count),
  };
}

function reduceClusterProperties(accumulated, properties) {
  accumulated.sum_total += toNumber(properties.sum_total);
  accumulated.sum_sick += toNumber(properties.sum_sick);
  accumulated.healthy_count += toNumber(properties.healthy_count);
  accumulated.people_sick_count += toNumber(properties.people_sick_count);
}

function categoryConfig(category) {
  const config = CLUSTER_CATEGORIES[category];
  if (!config) throw new Error(`Unknown cluster category: ${category}`);
  return config;
}

function peopleClusterRadius(total) {
  return interpolateStops(total, [
    [10, 16],
    [200, 24],
    [1000, 34],
    [4000, 46],
  ]);
}

function categoryClusterRadius(total) {
  return interpolateStops(total, [
    [2, 20],
    [10, 25],
    [50, 32],
    [200, 40],
  ]);
}

function interpolateStops(value, stops) {
  const x = Number(value);
  if (!Number.isFinite(x)) return stops[0][1];
  if (x <= stops[0][0]) return stops[0][1];
  for (let index = 0; index < stops.length - 1; index += 1) {
    const [x0, y0] = stops[index];
    const [x1, y1] = stops[index + 1];
    if (x <= x1) {
      const t = (x - x0) / (x1 - x0);
      return y0 + (y1 - y0) * t;
    }
  }
  return stops[stops.length - 1][1];
}

function sickRateCssColor(rate) {
  const stops = [
    [0.43, [254, 204, 92]],
    [0.58, [253, 141, 60]],
    [0.73, [189, 0, 38]],
  ];
  const value = Number(rate);
  if (!Number.isFinite(value)) return "#94a3b8";
  const toRgb = ([r, g, b]) => `rgb(${r}, ${g}, ${b})`;
  if (value <= stops[0][0]) return toRgb(stops[0][1]);
  if (value >= stops[stops.length - 1][0]) return toRgb(stops[stops.length - 1][1]);
  for (let index = 0; index < stops.length - 1; index += 1) {
    const [x0, c0] = stops[index];
    const [x1, c1] = stops[index + 1];
    if (value >= x0 && value <= x1) {
      const t = (value - x0) / (x1 - x0);
      return toRgb(c0.map((channel, channelIndex) => Math.round(channel + (c1[channelIndex] - channel) * t)));
    }
  }
  return "#94a3b8";
}

function toNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

function cloneJson(value) {
  return value == null ? value : JSON.parse(JSON.stringify(value));
}
